import React, { useCallback, useEffect, useState } from 'react';
import PageWayfinding from '../../components/common/PageWayfinding';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import useDiagnosticForm, { STEP, STEP_LABELS, STEP_ORDER } from '../../features/diagnostic/state/useDiagnosticForm';
import RegistrationModal from '../../features/diagnostic/components/RegistrationModal';
import { requestAiReport } from '../../features/diagnostic/ai/client';
import { exportDiagnosticReport } from '../../features/diagnostic/export/exportReport';
import { sanitizeRegistrationPayload } from '../../utils/inputSecurity';
import StepNiche from './steps/StepNiche';
import StepDealModel from './steps/StepDealModel';
import StepNumbers from './steps/StepNumbers';
import StepGoal from './steps/StepGoal';
import StepResult from './steps/StepResult';
import PaidReport from './PaidReport';
import '../../components/landing-v2/landing-v2-fonts';
import './Diagnostic.css';

const DOWNLOAD_INTENT = { FREE: 'free', PAID: 'paid' };

const DiagnosticPage = () => {
  const {
    state,
    niche,
    result,
    stepIndex,
    canProceed,
    setField,
    setNiche,
    setSimulation,
    resetSimulation,
    next,
    back,
    reset,
  } = useDiagnosticForm();

  const [isPaid, setIsPaid] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [pendingDownload, setPendingDownload] = useState(null);
  const [aiReport, setAiReport] = useState(null);
  const [aiState, setAiState] = useState('idle');
  const [showValidation, setShowValidation] = useState(false);

  useDocumentTitle('marketDir — диагностика бизнеса');

  useEffect(() => {
    setShowValidation(false);
  }, [state.step]);

  useEffect(() => {
    if (canProceed) setShowValidation(false);
  }, [canProceed]);

  const getBlockedHint = () => {
    switch (state.step) {
      case STEP.NICHE:
        return 'Выберите нишу';
      case STEP.DEAL_MODEL:
        return 'Выберите, как вам платят';
      case STEP.NUMBERS:
        return 'Заполните обязательные поля: выручка и число продаж';
      case STEP.GOAL:
        return 'Укажите целевую выручку';
      default:
        return 'Заполните обязательные поля';
    }
  };

  const handleNextClick = () => {
    if (!canProceed) {
      setShowValidation(true);
      return;
    }
    next();
  };

  const handleNextHover = () => {
    if (!canProceed) setShowValidation(true);
  };

  const loadAiReport = useCallback(async () => {
    if (!result || !result.valid) return;

    setAiState('loading');
    const { report, source } = await requestAiReport(result);
    setAiReport(report);
    setAiState(source === 'ai' ? 'ready' : 'fallback');
  }, [result]);

  const handleUnlock = useCallback(() => {
    setIsPaid(true);
    loadAiReport();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [loadAiReport]);

  const runDownload = useCallback(
    (intent, person) => {
      if (!result || !result.valid) return;

      exportDiagnosticReport({
        result,
        aiReport: intent === DOWNLOAD_INTENT.PAID ? aiReport : null,
        full: intent === DOWNLOAD_INTENT.PAID,
        person,
      });
    },
    [result, aiReport],
  );

  const requestDownload = useCallback(
    (intent) => {
      if (registration) {
        runDownload(intent, registration);
        return;
      }
      setPendingDownload(intent);
    },
    [registration, runDownload],
  );

  const handleRegistrationSubmit = useCallback(
    (values) => {
      const safe = sanitizeRegistrationPayload(values);
      setRegistration(safe);
      const intent = pendingDownload;
      setPendingDownload(null);
      runDownload(intent, safe);
    },
    [pendingDownload, runDownload],
  );

  const handleRestart = useCallback(() => {
    reset();
    setIsPaid(false);
    setAiReport(null);
    setAiState('idle');
  }, [reset]);

  useEffect(() => {
    if (!isPaid) return;
    setAiReport(null);
    setAiState('idle');
  }, [state.targetRevenue, isPaid]);

  const isResultStep = state.step === STEP.RESULT;
  const showNav = !isResultStep;

  const renderStep = () => {
    switch (state.step) {
      case STEP.NICHE:
        return (
          <StepNiche
            selectedId={state.nicheId}
            onSelect={setNiche}
            showValidation={showValidation}
          />
        );

      case STEP.DEAL_MODEL:
        return (
          <StepDealModel
            niche={niche}
            selectedId={state.dealModelId}
            onSelect={(value) => setField('dealModelId', value)}
            showValidation={showValidation}
          />
        );

      case STEP.NUMBERS:
        return (
          <StepNumbers
            niche={niche}
            state={state}
            result={result}
            setField={setField}
            showValidation={showValidation}
          />
        );

      case STEP.GOAL:
        return (
          <StepGoal
            state={state}
            result={result}
            setField={setField}
            showValidation={showValidation}
          />
        );

      case STEP.RESULT:
        if (!result || !result.valid) {
          return (
            <div className="dg-step">
              <p className="dg-note">Вернитесь на шаг назад и проверьте введённые цифры.</p>
            </div>
          );
        }

        return isPaid ? (
          <PaidReport
            result={result}
            aiReport={aiReport}
            aiState={aiState}
            onRetryAi={loadAiReport}
            onDownload={() => requestDownload(DOWNLOAD_INTENT.PAID)}
          />
        ) : (
          <StepResult
            result={result}
            state={state}
            setSimulation={setSimulation}
            resetSimulation={resetSimulation}
            onDownload={() => requestDownload(DOWNLOAD_INTENT.FREE)}
            onUnlock={handleUnlock}
          />
        );

      default:
        return null;
    }
  };

  return (
    <main className="dg-shell">
      <PageWayfinding showTools />

      <div className="dg-progress" aria-label="Шаги диагностики">
        {STEP_ORDER.map((step, index) => (
          <div
            key={step}
            className={`dg-progress-step${index <= stepIndex ? ' is-done' : ''}${
              index === stepIndex ? ' is-current' : ''
            }`}
          >
            <span className="dg-progress-dot" aria-hidden="true" />
            <span className="dg-progress-label">{STEP_LABELS[step]}</span>
          </div>
        ))}
      </div>

      <div className="dg-content">{renderStep()}</div>

      {showNav ? (
        <div className="dg-nav">
          {stepIndex > 0 ? (
            <button type="button" className="dg-btn dg-btn--ghost" onClick={back}>
              Назад
            </button>
          ) : (
            <span />
          )}

          <div className="dg-nav-next">
            {showValidation && !canProceed ? (
              <p className="dg-nav-hint" role="alert">
                {getBlockedHint()}
              </p>
            ) : null}
            <button
              type="button"
              className={`dg-btn dg-btn--primary${!canProceed ? ' is-blocked' : ''}`}
              aria-disabled={!canProceed}
              onClick={handleNextClick}
              onMouseEnter={handleNextHover}
              onFocus={handleNextHover}
            >
              {state.step === STEP.GOAL ? 'Показать результат' : 'Далее'}
            </button>
          </div>
        </div>
      ) : (
        <div className="dg-nav dg-nav--single">
          <button type="button" className="dg-link-btn" onClick={back}>
            Изменить цифры
          </button>
          <button type="button" className="dg-link-btn" onClick={handleRestart}>
            Начать заново
          </button>
        </div>
      )}

      <RegistrationModal
        open={Boolean(pendingDownload)}
        onClose={() => setPendingDownload(null)}
        onSubmit={handleRegistrationSubmit}
        title="Куда отправить результат"
        description="Заполните три поля — и файл откроется для скачивания."
      />
    </main>
  );
};

export default DiagnosticPage;
